import {
  Card,
  FieldGroup,
  Flex,
  IconButton,
  SelectField,
  SelectProps,
  TextField,
  TextInputProps
} from '@contentful/forma-36-react-components';
import React from 'react';
import PropTypes from 'prop-types';
import { mappingRowTypeIds, mappingRowTypeLabels, mappingRowTypes } from '../helpers/constants';

function getTextInputProps(mapping, mappingRowType: number): TextInputProps {
  return {
    width: 'medium',
    disabled:
      mappingRowType === mappingRowTypes.ATTRIBUTE
        ? mapping.attributeValueMappings && mapping.attributeValueMappings.length > 0
        : false
  };
}

// eslint-disable-next-line no-undef
function getSelectProps(mapping, mappingRowType: number): Partial<SelectProps> {
  return {
    width: 'medium',
    isDisabled: getIsSelectDisabled(mappingRowType, mapping)
  };
}

export const MappingRow = ({
  mapping,
  options,
  onSourceChange,
  onTargetChange,
  onRemoveClick,
  mappingRowType,
  isRequired,
  index = -1
}) => {
  return (
    <Card key={getCardKey(mappingRowType, index)} style={{ marginBottom: '10px' }}>
      <FieldGroup>
        <Flex justifyContent="space-between" alignItems="center" marginBottom="spacingM">
          <Flex>
            <TextField
              textInputProps={getTextInputProps(mapping, mappingRowType)}
              required={isRequired}
              name={getTextFieldId(mappingRowType, index)}
              id={getTextFieldId(mappingRowType, index)}
              labelText={getTextFieldLabel(mappingRowType)}
              value={mapping.source}
              helpText={'Name of the source ' + mappingRowTypeLabels[mappingRowType].toLowerCase()}
              onChange={onSourceChange}
            />
          </Flex>
          <Flex>
            <span>-&gt;</span>
          </Flex>
          <Flex>
            <SelectField
              selectProps={getSelectProps(mapping, mappingRowType)}
              required={isRequired}
              name={getSelectFieldId(mappingRowType, index)}
              id={getSelectFieldId(mappingRowType, index)}
              labelText={getSelectFieldLabel(mappingRowType)}
              helpText={'Name of the target ' + mappingRowTypeLabels[mappingRowType].toLowerCase()}
              value={mapping.target}
              onChange={onTargetChange}>
              {options}
            </SelectField>
          </Flex>
          {getRemoveButton(onRemoveClick, index)}
        </Flex>
      </FieldGroup>
    </Card>
  );
};

MappingRow.propTypes = {
  mapping: PropTypes.object.isRequired,
  options: PropTypes.array,
  onSourceChange: PropTypes.func.isRequired,
  onTargetChange: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func,
  mappingRowType: PropTypes.number,
  isRequired: PropTypes.bool,
  index: PropTypes.number
};

function getCardKey(mappingRowType: number, index: number): React.Key | null | undefined {
  switch (mappingRowType) {
    case mappingRowTypes.ATTRIBUTE:
      return 'attribute_mapping';
    case mappingRowTypes.ATTRIBUTE_VALUE:
      return `attribute_value_mapping_${index}`;
    case mappingRowTypes.NEW_ATTRIBUTE_VALUE_MAPPING:
      return 'new_attribute_value_mapping';
    default:
      break;
  }
  return 'unknown';
}

interface MappingWithValues {
  attributeValueMappings?: { source?: string; target?: string }[];
  [key: string]: unknown;
}

function getIsSelectDisabled(mappingRowType: number, mapping: MappingWithValues): boolean | undefined {
  if (mappingRowType === mappingRowTypes.NEW_ATTRIBUTE_VALUE_MAPPING) {
    return false;
  }

  if (mappingRowType === mappingRowTypes.ATTRIBUTE_VALUE) {
    return mapping.source ? false : true;
  }

  return (mapping.attributeValueMappings?.length ?? 0) > 0;
}

function getRemoveButton(onRemoveClick: ((id: string) => void) | undefined, index: number) {
  if (onRemoveClick) {
    return (
      <Flex>
        <IconButton
          id={'removeAttributeValueMapping_' + index}
          buttonType="negative"
          iconProps={{ icon: 'Delete', size: 'medium' }}
          label="Remove attribute value mapping"
          onClick={() => onRemoveClick('removeAttributeValueMapping_' + index)}
        />
      </Flex>
    );
  }
}

function getTextFieldId(mappingRowType: number, index: number): string {
  return 'source' + mappingRowTypeIds[mappingRowType] + '_' + index;
}

function getSelectFieldId(mappingRowType: number, index: number): string {
  return 'target' + mappingRowTypeIds[mappingRowType] + '_' + index;
}

function getTextFieldLabel(mappingRowType: number): string {
  return 'Source ' + mappingRowTypeLabels[mappingRowType];
}

function getSelectFieldLabel(mappingRowType: number): string {
  return 'Target ' + mappingRowTypeLabels[mappingRowType];
}
